/**
 * Map-ready tract shapes for the deals map: joined tract rows -> grouped,
 * status-coloured aliquot features. Pure.
 *
 * GROUPING. Several deals often chase the same land. Tracts are grouped by
 * section + aliquot (`aliquotKey`, so "NE, E2NW" and "E/2 NW/4 & NE/4" are
 * one shape); each group is drawn once, coloured by the most advanced LIVE
 * status bucket among its deals, with a count badge when it holds more than
 * one deal.
 *
 * FLAGS on each group:
 *   unresolved  the quartering can't be read -> the whole section is drawn
 *               as a dashed outline with no fill
 *   approximate the section is lotted / irregular (isLottedSection) -> the
 *               aliquot is only indicative, drawn with a dotted edge
 */
import { aliquotKey, isLottedSection, tractFeatures } from "./aliquot";
import { polygonLabelPoint } from "./mapLabels";
import { mostAdvancedBucket, statusBucket } from "./tractStatusBucket";

const fc = (features = []) => ({ type: "FeatureCollection", features });

/**
 * @param {Array<object>} rows  joined tract rows (dealTracts.joinTractRows)
 * @param {Map<string, object>} sectionIndex  indexSectionsByTrs(sections)
 * @param {{ includeDead?: boolean }} [options]  dead deals are left out
 *   unless includeDead
 * @returns {{
 *   fillFC: object,        resolved groups (fill + bucket colour)
 *   lineFC: object,        resolved, exact groups (solid edge)
 *   approxLineFC: object,  resolved, approximate groups (dotted edge)
 *   unresolvedFC: object,  unreadable quartering (dashed section outline)
 *   badgeFC: object,       Point per group with more than one deal
 *   groups: Map<string, object[]>,      group_id -> the tract rows in it
 *   shapesByDeal: Map<string, object[]> deal_id -> that deal's features
 * }}
 */
export const buildTractShapes = (rows, sectionIndex, options = {}) => {
  const includeDead = !!options.includeDead;
  const out = {
    fillFC: fc(),
    lineFC: fc(),
    approxLineFC: fc(),
    unresolvedFC: fc(),
    badgeFC: fc(),
    groups: new Map(),
    shapesByDeal: new Map(),
  };
  const shown = (Array.isArray(rows) ? rows : [])
    .map((r) => ({ ...r, status_bucket: statusBucket(r?.deal_status) }))
    .filter((r) => includeDead || r.status_bucket !== "dead");

  // One feature per tract (geometry), in input order.
  const feats = tractFeatures(shown, sectionIndex);
  const byGroup = new Map();
  for (const f of feats) {
    const p = f.properties;
    const ak = aliquotKey(p.quartering);
    const groupId = `${p.trs_canonical}|${ak === null ? "unresolved" : ak}`;
    let g = byGroup.get(groupId);
    if (!g) {
      g = { geometry: f.geometry, rows: [], unresolved: ak === null };
      byGroup.set(groupId, g);
    }
    g.rows.push(p);
    const dealKey = String(p.deal_id);
    if (!out.shapesByDeal.has(dealKey)) out.shapesByDeal.set(dealKey, []);
    out.shapesByDeal.get(dealKey).push(f);
  }

  for (const [groupId, g] of byGroup.entries()) {
    const dealIds = new Set(g.rows.map((r) => r.deal_id));
    const sec = g.rows[0].trs_canonical.split("-")[2];
    const properties = {
      group_id: groupId,
      trs_canonical: g.rows[0].trs_canonical,
      bucket: mostAdvancedBucket(g.rows.map((r) => r.status_bucket)),
      deal_count: dealIds.size,
      unresolved: g.unresolved,
      approximate: !g.unresolved && isLottedSection(sec),
    };
    const feature = { type: "Feature", geometry: g.geometry, properties };
    out.groups.set(groupId, g.rows);
    if (g.unresolved) {
      out.unresolvedFC.features.push(feature);
    } else {
      out.fillFC.features.push(feature);
      (properties.approximate ? out.approxLineFC : out.lineFC).features.push(feature);
    }
    if (dealIds.size > 1) {
      const pt = polygonLabelPoint(g.geometry);
      if (pt) {
        out.badgeFC.features.push({
          type: "Feature",
          geometry: { type: "Point", coordinates: pt },
          properties: { group_id: groupId, count: dealIds.size },
        });
      }
    }
  }
  return out;
};
